import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";

import { getIo, getCampaigns, generateCampaign, dryRunCampaign, dryRunPreview, getCampaignStatus, ApiError } from "../api.js";
import { getCurrentUser } from "./LoginPage.jsx";
import StatusBadge from "./StatusBadge.jsx";
import AmendmentDiff from "./AmendmentDiff.jsx";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(n, currency = "USD") {
  if (n == null) return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (Number.isNaN(num)) return String(n);
  try {
    return num.toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 2 });
  } catch {
    return `$${num.toFixed(2)}`;
  }
}

// Build a preview hierarchy tree from an IO record: Campaign -> Ads (line
// items) -> Creatives (creative_dimensions / specs). This is the structure
// the Campaign Builder Agent would create on the target platform.
function buildHierarchy(io) {
  if (!io) return null;
  return {
    type: "Campaign",
    name: io.campaign_name || `${io.advertiser_name} — ${io.io_number}`,
    io_number: io.io_number,
    budget: io.total_gross_budget,
    currency: io.currency,
    flight: [io.contract_start_date, io.contract_end_date],
    children: (io.line_items || []).map((li) => ({
      type: "Ad",
      name: li.line_item_name,
      line_item_id: li.line_item_id,
      ad_format: li.ad_format,
      pricing_model: li.pricing_model,
      rate: li.rate,
      units: li.units,
      gross_budget: li.gross_budget,
      flight: [li.flight_start_date, li.flight_end_date],
      children: [
        ...(li.creative_dimensions || []).map((dim) => ({
          type: "Creative",
          name: dim,
          line_item_id: li.line_item_id,
          dimension: dim,
          specs: li.creative_specs || "",
        })),
        // If no explicit dimensions, still show one Creative placeholder.
        ...((!li.creative_dimensions || li.creative_dimensions.length === 0)
          ? [{
              type: "Creative",
              name: "Creative spec",
              line_item_id: li.line_item_id,
              dimension: "—",
              specs: li.creative_specs || "",
            }]
          : []),
      ],
    })),
  };
}

// Recursively render the hierarchy tree.
function TreeNode({ node, depth = 0 }) {
  if (!node) return null;
  const typeClass = `tree-type-${node.type.toLowerCase()}`;
  return (
    <li className={`tree-node ${typeClass}`} style={{ marginLeft: depth * 18 }}>
      <div className="tree-row">
        <span className="tree-type-badge">{node.type}</span>
        <span className="tree-name">{node.name}</span>
        {node.ad_format && <span className="tree-tag">{node.ad_format}</span>}
        {node.pricing_model && <span className="tree-tag">{node.pricing_model}</span>}
        {node.gross_budget != null && (
          <span className="tree-budget">{formatCurrency(node.gross_budget)}</span>
        )}
        {node.rate != null && <span className="tree-tag">@ {node.rate}</span>}
        {node.units != null && <span className="tree-tag">{node.units.toLocaleString()} units</span>}
        {node.dimension && node.dimension !== "—" && (
          <span className="tree-tag">{node.dimension}</span>
        )}
      </div>
      {node.children && node.children.length > 0 && (
        <ul className="tree-children">
          {node.children.map((child, i) => (
            <TreeNode key={`${node.type}-${i}`} node={child} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CampaignPreview() {
  const { id } = useParams();

  const [io, setIo] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [generationResult, setGenerationResult] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [pollTimer, setPollTimer] = useState(null);

  // -------------------------------------------------------------------------
  // Load IO + existing campaigns for this IO.
  // -------------------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ioData, campData] = await Promise.all([
        getIo(id),
        getCampaigns({ ioId: id, limit: 50 }).catch(() => ({ data: [] })),
      ]);
      setIo(ioData);
      setCampaigns(campData.data || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    return () => {
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [id]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  async function handleDryRun() {
    if (!io) return;
    setBusy(true);
    try {
      // Use the synchronous dry-run endpoint — no campaign job or API key needed
      const result = await dryRunPreview(id);
      setDryRunResult(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerate() {
    if (!io) return;
    setBusy(true);
    setError(null);
    try {
      const result = await generateCampaign(id, getCurrentUser());
      setGenerationResult(result);
      startPolling(result.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function startPolling(campaignId) {
    if (pollTimer) clearTimeout(pollTimer);
    const timer = setTimeout(async () => {
      try {
        const status = await getCampaignStatus(campaignId);
        setGenerationStatus(status);
        if (status.status === "pending" || status.status === "dry_run_pending" || status.status === "generating") {
          startPolling(campaignId);
        }
      } catch {
        // stop polling on error
      }
    }, 3000);
    setPollTimer(timer);
  }

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------
  const hierarchy = useMemo(() => buildHierarchy(io), [io]);
  const campaign = campaigns[0] || generationResult || null;
  const campaignStatus = generationStatus?.status || campaign?.status || null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) return <div className="campaign-preview container"><p>Loading IO…</p></div>;
  if (error && !io) {
    return (
      <div className="campaign-preview container">
        <div className="alert alert-error">{error}</div>
        <Link className="btn" to="/dashboard">Back to Dashboard</Link>
      </div>
    );
  }
  if (!io) return <div className="campaign-preview container"><p>IO not found.</p></div>;

  return (
    <div className="campaign-preview container">
      {/* Header */}
      <div className="preview-header">
        <div className="preview-header-left">
          <h2>Campaign Preview · <span className="mono">{io.io_number}</span></h2>
          <StatusBadge status={io.status} />
          <span className="muted">{io.advertiser_name} · {io.agency}</span>
        </div>
        <div className="preview-header-actions">
          <Link className="btn" to={`/ios/${encodeURIComponent(id)}`}>← Back to IO</Link>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Approval gate warning */}
      {io.status !== "approved" && (
        <div className="alert alert-warning">
          This IO is not approved yet (status: {io.status}). Campaign generation requires the
          IO to be approved first. <Link to={`/ios/${encodeURIComponent(id)}`}>Review & approve →</Link>
        </div>
      )}

      <div className="preview-grid">
        {/* LEFT: hierarchy tree */}
        <div className="preview-tree">
          <h4>Campaign Hierarchy</h4>
          <p className="muted">
            This is the structure that would be created on the target platform
            (Campaign → Ads → Creatives).
          </p>
          {hierarchy && (
            <ul className="tree-root">
              <TreeNode node={hierarchy} />
            </ul>
          )}
        </div>

        {/* RIGHT: actions + results */}
        <div className="preview-actions">
          <h4>Generation</h4>

          <div className="preview-buttons">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleDryRun}
              disabled={busy || io.status !== "approved"}
              title="Preview the request tree without committing"
            >
              {busy ? "Working…" : "Run Dry-Run"}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleGenerate}
              disabled={busy || io.status !== "approved"}
            >
              {busy ? "Generating…" : "Generate Campaign"}
            </button>
          </div>

          {/* Dry-run result */}
          {dryRunResult && (
            <div className="preview-result">
              <h5>Dry-Run Result</h5>
              <div className="result-box">
                <div><strong>Job:</strong> <span className="mono">{dryRunResult.id}</span></div>
                <div><strong>Status:</strong> {dryRunResult.status}</div>
                <div><strong>Dry run:</strong> {String(dryRunResult.dryRun ?? true)}</div>
                <pre className="result-json">{JSON.stringify(dryRunResult, null, 2)}</pre>
              </div>
            </div>
          )}

          {/* Generation result */}
          {campaign && (
            <div className="preview-result">
              <h5>Generation Job</h5>
              <div className="result-box">
                <div><strong>Campaign ID:</strong> <span className="mono">{campaign.id}</span></div>
                <div><strong>IO ID:</strong> <span className="mono">{campaign.ioId}</span></div>
                <div>
                  <strong>Status:</strong>{" "}
                  <StatusBadge status={campaignStatus || campaign.status} />
                </div>
                {campaign.created_at && <div><strong>Created:</strong> {new Date(campaign.created_at).toLocaleString()}</div>}
              </div>
            </div>
          )}

          {/* When generation completes, surface created IDs (if provided). */}
          {generationStatus && (generationStatus.status === "live" || generationStatus.status === "completed") && (
            <div className="preview-result result-success">
              <h5>Generated Objects</h5>
              <ul className="generated-ids">
                <li><strong>campaign_id:</strong> <span className="mono">{generationStatus.campaign_id || campaign?.id || "—"}</span></li>
                <li><strong>ad_ids:</strong> <span className="mono">{(generationStatus.ad_ids || []).join(", ") || "—"}</span></li>
                <li><strong>creative_ids:</strong> <span className="mono">{(generationStatus.creative_ids || []).join(", ") || "—"}</span></li>
                <li><strong>status:</strong> {generationStatus.status}</li>
              </ul>
            </div>
          )}

          {/* Amendment diff (if applicable) */}
          {io.amendmentResult && (
            <div className="preview-amendment">
              <h5>Amendment Diff</h5>
              <AmendmentDiff amendmentResult={io.amendmentResult} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}